import { CheckIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import React from 'react';
import { Checkbox } from 'react-aria-components';
import { AssetContext } from './asset.context-provider';
import { BUTTON_TOP_INSET } from './constants';

interface AssetCheckboxProps {
  /**
   * Whether the asset is in the current selection.
   *
   * @required
   */
  isSelected: boolean;
  /**
   * Called with the new state. `shiftKey` is true when the user held Shift, so the caller can select a range.
   *
   * @required
   */
  onSelectedChange: (isSelected: boolean, options: { shiftKey: boolean }) => void;
  /**
   * Keeps the checkbox visible without hover, e.g. while something is selected, so the user can see what to click.
   *
   * @optional
   * @default false
   */
  isAlwaysVisible?: boolean;
  /**
   * Accessible name, e.g. "Select Lifestyle shot".
   *
   * @optional
   * @default 'Select image'
   */
  'aria-label'?: string;
}

/**
 * Selection checkbox in the top left corner of an asset tile, opposite of `Asset.Actions`. It shows on hover and
 * keyboard focus like the actions, and stays visible while checked or `isAlwaysVisible`. Pressing it doesn't open the
 * asset.
 *
 * @example
 * <Asset.Checkbox isSelected={isSelected(id)} onSelectedChange={(_, { shiftKey }) => toggle(id, shiftKey)} />
 */
function AssetCheckbox(props: AssetCheckboxProps) {
  const { isSelected, onSelectedChange, isAlwaysVisible = false, 'aria-label': ariaLabel = 'Select image' } = props;
  const { buttonTopOffset, isHovered, isFocusVisible, isViewOnly } = React.useContext(AssetContext);
  // React Aria reports only the new state, so Shift is read from the pointer or key event that caused the change
  const shiftKeyRef = React.useRef(false);

  if (isViewOnly) return null;

  const isVisible = isSelected || isAlwaysVisible || isHovered || isFocusVisible;

  return (
    // Visibility lives on the wrapper and uses CSS `:hover` of the tile, not React hover state: CSS applies before the
    // click lands, so a fast click on the corner selects instead of falling through to the tile and opening the asset
    <div
      className={cx(
        'absolute left-1 z-10 transition-opacity duration-150',
        '[:hover>&]:opacity-100 [:hover>&]:pointer-events-auto',
        // Keyboard focus and coarse pointers (no hover on touch) always see it, same as the hover actions
        '[&:has([data-focus-visible])]:opacity-100 [&:has([data-focus-visible])]:pointer-events-auto',
        '[@media(pointer:coarse)]:opacity-100 [@media(pointer:coarse)]:pointer-events-auto',
        isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
      )}
      style={{ top: buttonTopOffset ?? `var(--asset-button-top, ${BUTTON_TOP_INSET}px)` }}
      onPointerDownCapture={(event) => {
        shiftKeyRef.current = event.shiftKey;
      }}
      onKeyDownCapture={(event) => {
        shiftKeyRef.current = event.shiftKey;
      }}
    >
      <Checkbox
        aria-label={ariaLabel}
        isSelected={isSelected}
        onChange={(next) => {
          // Read once and reset, so a later activation without a new pointer or key event can't reuse an old Shift
          const shiftKey = shiftKeyRef.current;
          shiftKeyRef.current = false;
          onSelectedChange(next, { shiftKey });
        }}
        className={cx(
          'group flex h-9 w-9 cursor-pointer items-center justify-center outline-none transition-colors duration-150 sm:h-18 sm:w-18',
          'bg-overlay-action text-quinary data-[hovered]:bg-primary data-[hovered]:text-tertiary',
          'data-[selected]:bg-brand-600 data-[selected]:text-primary-on-brand',
          'data-[focus-visible]:shadow-hover-action-button-focused',
        )}
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center border border-current group-data-[selected]:border-transparent"
        >
          <CheckIcon size={12} className="hidden group-data-[selected]:block" />
        </span>
      </Checkbox>
    </div>
  );
}

export { AssetCheckbox };
export type { AssetCheckboxProps };
